import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload } from "lucide-react";
import { HoloPanel } from "../components/HoloPanel";
import { RatingStars } from "../components/RatingStars";
import { VenueSheet } from "../components/VenueSheet";
import { CallConfirmSheet } from "../components/CallConfirmSheet";
import { CallOutcomeSheet } from "../components/CallOutcomeSheet";
import { BookingSheet } from "../components/BookingSheet";
import { ImportCsvSheet } from "../components/ImportCsvSheet";
import { api, errorMessage } from "../lib/api";
import { primaryContactAction } from "../lib/contactAction";

export default function VenuesPage({ session, bookingMode = false }) {
  const navigate = useNavigate();
  const commercial = session.user.role === "commerciale";
  const segments = commercial ? [["miei", "Miei"], ["tutti", "Tutti"], ["contattati", "Contattati"], ["storico", "Storico"]]
    : [["da_assegnare", "Da assegnare"], ["in_corso", "In corso"], ["tutti", "Tutti"], ["contattati", "Contattati"], ["storico", "Storico"]];
  const [view, setView] = useState(commercial ? "miei" : "da_assegnare");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [callIntent, setCallIntent] = useState(null);
  const [callOutcome, setCallOutcome] = useState(null);
  const [callBooking, setCallBooking] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((previous) => previous + 1), []);
  useEffect(() => { api.get("/venues/categories").then(({ data }) => setCategories(data.items)).catch(() => {}); }, [revision]);

  useEffect(() => {
    let live = true;
    setLoading(true); setError("");
    const timer = setTimeout(() => {
      api.get("/venues", { params: { view, q: search, activity_type: category || undefined, offset: 0 } }).then(({ data }) => {
        if (!live) return;
        setItems(data.items); setTotal(data.total); setHasMore(data.has_more);
      }).catch((e) => { if (live) setError(errorMessage(e)); }).finally(() => { if (live) setLoading(false); });
    }, 180);
    return () => { live = false; clearTimeout(timer); };
  }, [view, search, category, revision]);

  const loadMore = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/venues", { params: { view, q: search,
        activity_type: category || undefined, offset: items.length } });
      setItems((previous) => [...previous, ...data.items]); setHasMore(data.has_more);
    } catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="venues-page" data-testid={bookingMode ? "page-fissa" : "page-locali"}>
      <div className="venues-search-line">
        <input className="venues-search" type="search" placeholder="Cerca un locale" aria-label="Cerca un locale" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="venue-search-input" />
        {!commercial && !bookingMode && <button type="button" className="import-trigger" onClick={() => setImportOpen(true)} aria-label="Importa CSV" title="Importa CSV" data-testid="import-open-button"><Upload size={21} aria-hidden="true" /></button>}
      </div>
      <div className={`venue-segments${commercial ? " compact" : ""}`} role="group" aria-label="Filtra locali" data-testid="venue-filters">
        {segments.map(([key, label]) => <button key={key} type="button" className={view === key ? "active" : ""} aria-pressed={view === key} onClick={() => setView(key)} data-testid={`venue-filter-${key.replaceAll("_", "-")}`}>{label}</button>)}
      </div>
      {!!categories.length && <div className="venue-type-filter"><label htmlFor="venue-category-filter">TIPOLOGIA</label>
        <select id="venue-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
          data-testid="venue-category-filter"><option value="">Tutte le attività</option>
          {categories.map((term) => <option key={term} value={term}>{term}</option>)}</select></div>}
      <HoloPanel className="venue-list-panel" testId="venue-list-panel">
        <div className="venue-list-heading"><span data-testid="venue-count">{total} LOCALI</span><span data-testid="venue-sort-label">per voto</span></div>
        {error && <p className="form-error" role="alert" data-testid="venue-list-error">{error}</p>}
        {!error && !items.length && !loading && <p className="venue-list-empty" data-testid="venue-list-empty">Nessun locale da mostrare.</p>}
        <div data-testid="venue-list">{items.map((venue) => {
          const action = primaryContactAction(venue);
          const canCall = (!commercial || venue.assigned_salesperson_id === session.user.id)
            && !venue.archived_at && !["Cliente", "Firmato", "Perso", "Irraggiungibile", "Non interessato", "Non in target"].includes(venue.status);
          return <div className="venue-list-entry" key={venue.id} data-testid={`venue-entry-${venue.id}`}>
            <button type="button" className="venue-list-row" onClick={() => setSelected(venue.id)} data-testid={`venue-row-${venue.id}`}>
              <span className="venue-list-copy"><strong data-testid={`venue-name-${venue.id}`}>{venue.name.split("|")[0].trim()}</strong><small data-testid={`venue-status-${venue.id}`}>{venue.status}{venue.archived_at ? " · Escluso" : ""}{venue.assigned_salesperson_name ? ` · ${venue.assigned_salesperson_name}` : ""}</small></span>
              <RatingStars rating={venue.rating} testId={`venue-stars-${venue.id}`} />
            </button>
            <div className="venue-list-phone">{action ? <span data-testid={`venue-phone-${venue.id}`}>{action.display} <small>· {action.source}</small></span>
              : <span className="contact-missing" data-testid={`venue-phone-missing-${venue.id}`}>telefono non trovato</span>}
              {action && <button type="button" disabled={!canCall} onClick={() => setCallIntent({ venue, action })}
                data-testid={`venue-call-${venue.id}`}>Chiama</button>}</div>
          </div>;
        })}</div>
        {hasMore && <button className="load-more" type="button" disabled={loading} onClick={loadMore} data-testid="venue-load-more-button">{loading ? "Caricamento…" : "Mostra altri"}</button>}
      </HoloPanel>
      <VenueSheet venueId={selected} session={session} bookingMode={bookingMode} onClose={() => setSelected(null)} onChanged={reload} />
      <CallConfirmSheet selection={callIntent} session={session} onClose={() => setCallIntent(null)}
        onStarted={() => { setCallIntent(null); reload(); }}
        onReturned={(record) => { const intent = callIntent;
          setCallOutcome({ ...intent, callId: record.id }); }} />
      <CallOutcomeSheet venue={callOutcome?.venue} callId={callOutcome?.callId} callAction={callOutcome?.action}
        session={session} onClose={() => setCallOutcome(null)}
        onSaved={() => { setCallOutcome(null); reload(); }}
        onBook={() => { const venueId = callOutcome.venue.id; setCallOutcome(null);
          window.setTimeout(() => setCallBooking({ venueId }), 180); }} />
      <BookingSheet selection={callBooking} session={session} onClose={() => setCallBooking(null)}
        onBooked={reload} onAgenda={() => { setCallBooking(null); navigate("/agenda"); }} />
      {!commercial && <ImportCsvSheet open={importOpen} onOpenChange={setImportOpen} csrfToken={session.csrf_token} onImported={reload} />}
    </div>
  );
}